import type { MonitoringIndicatorsLoading } from '../hooks/useMonitoringIndicators';
import type { TileKey } from '../monitoringUtils';
import useFmText from './useFmText';
import './SummaryTiles.scss';

interface ISummaryTilesProps {
    counts: Record<TileKey, number>;
    loading: MonitoringIndicatorsLoading;
    activeTile: TileKey | null;
    onSelect: (tile: TileKey | null) => void;
}

const TILE_TONE: Partial<Record<TileKey, string>> = {
    atrasada: 'fm-bad',
    conn1: 'fm-good',
    conn0: 'fm-bad',
    vt1: 'fm-good',
    vt0: 'fm-bad',
};

const SummaryTiles = ({ counts, loading, activeTile, onSelect }: ISummaryTilesProps) => {
    const { t, formatNumber } = useFmText();

    const isLoading = (tile: TileKey) => {
        if (tile === 'atrasada') {
            return loading.delayed;
        }
        if (tile === 'conn1' || tile === 'conn0') {
            return loading.connectivity;
        }
        if (tile === 'vt1' || tile === 'vt0') {
            return loading.volksTotal;
        }
        return loading.maintenance;
    };

    const renderButton = (tile: TileKey) => (
        <button
            type='button'
            key={tile}
            className={`fm-tbtn ${TILE_TONE[tile] ?? ''}`}
            aria-pressed={activeTile === tile}
            disabled={isLoading(tile)}
            onClick={() => onSelect(activeTile === tile ? null : tile)}
        >
            {isLoading(tile) ? (
                <span className='fm-skel' style={{ width: 48, height: 22 }} />
            ) : (
                <b className='fm-num'>{formatNumber(counts[tile])}</b>
            )}
            <span>{t(`tiles.${tile}`)}</span>
        </button>
    );

    const renderSplit = (positive: TileKey, negative: TileKey, hintKey: string) => {
        if (isLoading(positive)) {
            return null;
        }
        const total = counts[positive] + counts[negative];
        const pct = total ? Math.round((counts[positive] / total) * 100) : 0;
        return (
            <>
                <div className='fm-split'>
                    <i style={{ width: `${pct}%` }} />
                </div>
                <small>{t(hintKey, { pct })}</small>
            </>
        );
    };

    return (
        <div className='fm-tiles'>
            <div className='fm-panel fm-tile'>
                <h2>{t('tiles.maintenanceHeader')}</h2>
                <div className='fm-tile-opts'>
                    {(['agendar', 'atrasada', 'agendada', 'manut'] as TileKey[]).map(renderButton)}
                </div>
            </div>
            <div className='fm-panel fm-tile'>
                <h2>{t('tiles.connectivityHeader')}</h2>
                <div className='fm-tile-opts'>{(['conn1', 'conn0'] as TileKey[]).map(renderButton)}</div>
                {renderSplit('conn1', 'conn0', 'tiles.connectivityHint')}
            </div>
            <div className='fm-panel fm-tile'>
                <h2>{t('tiles.volksTotalHeader')}</h2>
                <div className='fm-tile-opts'>{(['vt1', 'vt0'] as TileKey[]).map(renderButton)}</div>
                {renderSplit('vt1', 'vt0', 'tiles.volksTotalHint')}
            </div>
        </div>
    );
};

export default SummaryTiles;
